'use client';

import type { useUserCriteria } from '@/app/hooks/useUserCriteria';
import type { User } from '@/types/user/type';
import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import type { UserType } from '~/model/UserType';
import { VpButton, VpFormControl, VpFormError, VpFormLabel, VpIcon, VpInput } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Criteria } from '@/app/[locale]/profile/components/Criteria/Criteria';
import { fetchItem } from '@/app/actions/server';
import { Loader } from '@/app/components/Loader';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { useCriterion } from '@/app/hooks/useCriterion';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { buttonValidation } from '~/components/commons/CompleteProfileBanner/utils';
import { MainColumn } from '~/components/commons/MainColumn/MainColumn';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { InfoBar } from '~/components/Journey/InfoBar/InfoBar';
import { getMatchingUserCriteria } from '~/components/Profile/utils';
import { useRequestState } from '~/hooks/request/useRequestState';
import { mandatoryCriteriaFlags } from '~/utils/criteria/mandatory';

export const MandatoryCriteriaForm = ({
  criteria,
  isLoading,
  lastInformation = false,
  userCriteria,
  user,
  submit,
}: {
  criteria: CriteriaType[];
  isLoading: boolean;
  lastInformation?: boolean;
  userCriteria: CriteriaChoiceUserType[];
  user: UserType;
  submit: ReturnType<typeof useUserCriteria>['submit'];
}) => {
  const t = useTranslations();
  const { setUser } = useAuthenticatedContext();

  const { startRequest, handleSuccess, handleError, status } = useRequestState();

  const isPending = status === 'pending';

  const { updatingData, onChange } = useCriterion({ userCriteria });

  /**
   * States
   */
  const [firstname, setFirstname] = useState<UserType['firstname']>(user?.firstname);
  const [lastname, setLastname] = useState<UserType['lastname']>(user?.lastname);
  const [hasFirstnameBeenFocused, setHasFirstnameBeenFocused] = useState(false);
  const [hasLastnameBeenFocused, setHasLastnameBeenFocused] = useState(false);

  /**
   * Variables
   */
  const errorMessage = t('PROFILE.CONTENT.MANDATORY_DATA');

  // List Flags from existing user criterias
  const userCriteriaFlags = userCriteria?.map(uc => uc.criterion.flag) || [];

  // List Flags from updating criterias (only filled ones)
  const updatingCriteriasFlags = Object.values(updatingData).map(
    ucv =>
      criteria?.find(mc => mc['@id'] === ucv.criteriaUri && !!ucv.value)?.flag,
  );

  const deletedMandatoryElement = Object.values(updatingData).some(
    element => element.value === null,
  );

  // Check if every mandatory criteria flag is set in the form or already existing
  const areThereMissingcriteria = !mandatoryCriteriaFlags.every(f =>
    [...updatingCriteriasFlags, ...userCriteriaFlags].includes(f),
  );

  const areThereMissingUserFields = user?.child
    ? !firstname
    : !firstname || !lastname;

  const areThereMissingMandatoryFields
    = areThereMissingUserFields
      || areThereMissingcriteria
      || deletedMandatoryElement;

  const buttonValidationInfo = buttonValidation(lastInformation);

  const inputForbiddenChars = /[!#$%&'()*+,./:;<=>?@[\]^_`{|}~]/g;
  const inputMaxLength = 40;

  const errorToDisplayed = (value: string, hasBeenFocused: boolean) => {
    return {
      empty: hasBeenFocused && !value,
      forbiddenChars: hasBeenFocused && inputForbiddenChars.test(value),
      maxLength: hasBeenFocused && value.length > inputMaxLength,
    };
  };

  const firstnameErrorToDisplayed = errorToDisplayed(firstname, hasFirstnameBeenFocused);
  const lastnameErrorToDisplayed = errorToDisplayed(lastname, hasLastnameBeenFocused);

  const isFirstnameErrorDisplayed = Object.values(firstnameErrorToDisplayed).some(reason => reason);
  const isLastnameErrorDisplayed = Object.values(lastnameErrorToDisplayed).some(reason => reason);

  const inputHelperText = (
    fieldname: string,
    value: string,
    error: ReturnType<typeof errorToDisplayed>,
  ) => {
    if (error.forbiddenChars) {
      const forbiddenCharsSet = new Set<string>();
      const forbiddenMatches = value.match(inputForbiddenChars);

      if (forbiddenMatches)
        forbiddenMatches.forEach(char => forbiddenCharsSet.add(char));

      return t('PROFILE.CONTENT.ERROR.FORBIDDEN_CHARS', {
        chars: Array.from(forbiddenCharsSet).join(''),
      });
    } else if (error.maxLength) {
      return t('PROFILE.CONTENT.ERROR.MAX_LENGTH', {
        fieldname: fieldname.toLowerCase(),
        max: inputMaxLength,
      });
    }

    return t('PROFILE.CONTENT.MANDATORY_DATA');
  };

  const handleSubmit = async () => {
    try {
      startRequest();

      await submit(updatingData);

      if (firstname !== user?.firstname || lastname !== user?.lastname) {
        const response = await fetchItem<User>({
          pathname: `users/${user.id}`,
          method: 'PUT',
          revalidate: 0,
          body: {
            firstname,
            lastname,
          },
        });

        if (!user.child) {
          setUser(response);
        }
      }

      handleSuccess();
    } catch (error) {
      handleError();
      throw error;
    }
  };

  return (
    <section className="profile-completion__page">
      <BeardmanTalking
        title={t('PROFILE.COMPLETION.MANDATORY_DATA.TITLE')}
        description={t('PROFILE.COMPLETION.MANDATORY_DATA.DESCRIPTION')}
      />
      <MainColumn column={journeyColumnTemplate}>
        <Loader loading={isLoading}>
          <div className="profile-completion__page__form">
            <div>
              <VpFormControl>
                <VpFormLabel>{t('PROFILE.CONTENT.FIRSTNAME')}</VpFormLabel>
                <VpInput
                  id="firstName"
                  name="firstName"
                  value={firstname || ''}
                  onChange={e => setFirstname(e.target.value)}
                  onBlur={() => setHasFirstnameBeenFocused(true)}
                />
                {isFirstnameErrorDisplayed && (
                  <VpFormError>
                    {inputHelperText(
                      t('PROFILE.CONTENT.FIRSTNAME'),
                      firstname,
                      firstnameErrorToDisplayed,
                    )}
                  </VpFormError>
                )}
              </VpFormControl>
            </div>
            {!user?.child && (
              <VpFormControl>
                <div>
                  <VpFormLabel>{t('PROFILE.CONTENT.LASTNAME')}</VpFormLabel>
                  <VpInput
                    id="lastName"
                    name="lastName"
                    value={lastname || ''}
                    onChange={e => setLastname(e.target.value)}
                    onBlur={() => setHasLastnameBeenFocused(true)}
                  />
                  {isLastnameErrorDisplayed && (
                    <VpFormError>
                      {inputHelperText(
                        t('PROFILE.CONTENT.LASTNAME'),
                        lastname,
                        lastnameErrorToDisplayed,
                      )}
                    </VpFormError>
                  )}
                </div>
              </VpFormControl>
            )}
            {criteria.map((criterion) => {
              const key = `criteria-${criterion?.id}`;
              const updatingUserValue = updatingData[criterion['@id']]?.value;
              const userCriterion = getMatchingUserCriteria(criterion, userCriteria);

              return (
                <div key={key}>
                  <Criteria
                    criteria={criterion}
                    userCriteria={userCriterion}
                    updatingUserValue={updatingUserValue}
                    handleChange={onChange}
                    required
                    helperText={errorMessage}
                  />
                </div>
              );
            })}
          </div>
          <div className="profile-completion__page__action">
            <VpButton
              data-cy="submitButton"
              disabled={isLoading || isPending || areThereMissingMandatoryFields}
              {...(buttonValidationInfo.icon && {
                endSlot: <VpIcon name={buttonValidationInfo.icon} />,
              })}
              onClick={handleSubmit}
            >
              {t(buttonValidationInfo.title)}
            </VpButton>
          </div>
          <InfoBar content={t('PROFILE.COMPLETION.COMMON.DATA_INFO')} />
        </Loader>
      </MainColumn>
    </section>
  );
};
